import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { apiClient } from "../App";
import { toast } from "sonner";
import { 
  ArrowLeft, RefreshCw, Check, X, Pencil, RotateCcw, 
  Megaphone, Clapperboard, Monitor, List, BarChart3, Columns,
  Calendar, Clock, ChevronRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const CATEGORIES = [
  "3D Modeling",
  "Exterior Renders",
  "Interior Renders",
  "Exterior 360s",
  "Interior 360s",
  "Animation",
  "VEXOS",
  "Marketing"
];

const CATEGORY_COLORS = {
  "3D Modeling": { bg: "bg-slate-100", text: "text-slate-700", bar: "bg-slate-500", border: "border-slate-200" },
  "Exterior Renders": { bg: "bg-amber-100", text: "text-amber-700", bar: "bg-amber-500", border: "border-amber-200" },
  "Interior Renders": { bg: "bg-blue-100", text: "text-blue-700", bar: "bg-blue-500", border: "border-blue-200" },
  "Exterior 360s": { bg: "bg-orange-100", text: "text-orange-700", bar: "bg-orange-500", border: "border-orange-200" },
  "Interior 360s": { bg: "bg-cyan-100", text: "text-cyan-700", bar: "bg-cyan-500", border: "border-cyan-200" },
  "Animation": { bg: "bg-purple-100", text: "text-purple-700", bar: "bg-purple-500", border: "border-purple-200" },
  "VEXOS": { bg: "bg-teal-100", text: "text-teal-700", bar: "bg-teal-500", border: "border-teal-200" },
  "Marketing": { bg: "bg-pink-100", text: "text-pink-700", bar: "bg-pink-500", border: "border-pink-200" },
};

// Editable Row Component for Table View
function EditableRow(props) {
  const [isEditing, setIsEditing] = useState(false);
  const [editQty, setEditQty] = useState(props.qty);
  const [editDays, setEditDays] = useState(props.perUnit);
  const [editCategory, setEditCategory] = useState(props.category);
  const [saving, setSaving] = useState(false);

  const colors = CATEGORY_COLORS[props.category] || CATEGORY_COLORS["Interior Renders"];

  function handleSave() {
    setSaving(true);
    props.onSave(props.deliverableId, {
      quantity: parseInt(editQty) || 1,
      production_days_per_unit: parseFloat(editDays) || 1,
      category: editCategory
    }).then(function() {
      setIsEditing(false);
      setSaving(false);
    }).catch(function() {
      setSaving(false);
    });
  }

  function handleCancel() {
    setEditQty(props.qty);
    setEditDays(props.perUnit);
    setEditCategory(props.category);
    setIsEditing(false);
  }

  if (isEditing) {
    return (
      <tr className="border-t border-border bg-blue-50/50">
        <td className="px-4 py-3 font-medium text-sm">{props.type}</td>
        <td className="text-center px-2 py-2">
          <Select value={editCategory} onValueChange={setEditCategory}>
            <SelectTrigger className="h-8 text-xs w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(function(cat) {
                return <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </td>
        <td className="text-center px-2 py-2">
          <Input type="number" value={editQty} onChange={function(e) { setEditQty(e.target.value); }} className="h-8 w-14 text-center text-xs" min="1" />
        </td>
        <td className="text-center px-2 py-2">
          <Input type="number" value={editDays} onChange={function(e) { setEditDays(e.target.value); }} className="h-8 w-16 text-center text-xs" step="0.5" min="0.5" />
        </td>
        <td className="text-center px-4 py-3 font-semibold text-sm">
          {((parseFloat(editDays) || 0) * (parseInt(editQty) || 0)).toFixed(1)}
        </td>
        <td className="px-4 py-2">
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={handleSave} disabled={saving} className="h-7 w-7 p-0">
              <Check className="w-4 h-4 text-green-600" />
            </Button>
            <Button size="sm" variant="ghost" onClick={handleCancel} className="h-7 w-7 p-0">
              <X className="w-4 h-4 text-red-600" />
            </Button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className={"group border-t border-border hover:bg-secondary/30 transition-colors" + (props.isCustom ? " bg-yellow-50/50" : "")}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{props.type}</span>
          {props.isCustom && <Badge className="text-[10px] bg-yellow-100 text-yellow-700 rounded-full">Custom</Badge>}
        </div>
      </td>
      <td className="text-center px-4 py-3">
        <Badge className={`rounded-full text-xs ${colors.bg} ${colors.text}`}>{props.category}</Badge>
      </td>
      <td className="text-center px-4 py-3 text-sm">{props.qty}</td>
      <td className="text-center px-4 py-3 text-sm">{props.perUnit}</td>
      <td className="text-center px-4 py-3 font-semibold text-sm">{props.totalDays}</td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-xs">{props.schedule}</span>
          <Button size="sm" variant="ghost" onClick={function() { setIsEditing(true); }} className="h-7 w-7 p-0 opacity-60 group-hover:opacity-100 transition-opacity">
            <Pencil className="w-3 h-3 text-gray-600" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

// Gantt Bar Component
function GanttBar(props) {
  const colors = CATEGORY_COLORS[props.category] || CATEGORY_COLORS["Interior Renders"];
  const leftPercent = (props.startDay / props.totalDays) * 100;
  const widthPercent = ((props.endDay - props.startDay) / props.totalDays) * 100;
  const barRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(null); // 'start' | 'end' | null
  const [tooltipData, setTooltipData] = useState(null); // { x, y, startDay, endDay, edge }

  // Calculate actual dates from program start date
  const calculateDate = (dayOffset) => {
    if (!props.programStartDate) {
      return `Day ${dayOffset}`;
    }
    const date = new Date(props.programStartDate);
    date.setDate(date.getDate() + dayOffset);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Handle drag start
  const handleDragStart = (e) => {
    if (!props.onDrag) return;
    setIsDragging(true);
    e.dataTransfer.setData('text/plain', JSON.stringify({
      deliverableId: props.deliverableId,
      startDay: props.startDay,
      endDay: props.endDay,
      category: props.category
    }));
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle resize start
  const handleResizeStart = (e, edge) => {
    if (!props.onResize) return;
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(edge);
    
    // Track final days in a ref for reliable access in mouseup
    let finalStartDay = props.startDay;
    let finalEndDay = props.endDay;
    
    // Initialize tooltip
    setTooltipData({
      x: e.clientX,
      y: e.clientY,
      startDay: props.startDay,
      endDay: props.endDay,
      edge: edge
    });
    
    const handleMouseMove = (moveEvent) => {
      if (!barRef.current) return;
      const container = barRef.current.closest('.flex-1');
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const mouseX = moveEvent.clientX - rect.left;
      const newDay = Math.round((mouseX / rect.width) * props.totalDays);
      
      if (edge === 'start') {
        finalStartDay = Math.max(0, Math.min(newDay, props.endDay - 1));
        props.onResize(props.deliverableId, finalStartDay, props.endDay);
      } else {
        finalEndDay = Math.max(props.startDay + 1, Math.min(newDay, props.totalDays));
        props.onResize(props.deliverableId, props.startDay, finalEndDay);
      }
      
      // Update tooltip position and data
      setTooltipData({
        x: moveEvent.clientX,
        y: moveEvent.clientY,
        startDay: finalStartDay,
        endDay: finalEndDay,
        edge: edge
      });
    };
    
    const handleMouseUp = () => {
      setIsResizing(null);
      setTooltipData(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      // Pass final days directly to onResizeEnd for reliable persistence
      if (props.onResizeEnd) {
        props.onResizeEnd(props.deliverableId, finalStartDay, finalEndDay);
      }
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Handle click to edit
  const handleClick = (e) => {
    if (isDragging || isResizing) return;
    if (props.onClick) {
      props.onClick(props.deliverableId);
    }
  };

  const durationDays = props.endDay - props.startDay;

  return (
    <>
      {/* Live tooltip during resize */}
      {tooltipData && (
        <div 
          className="fixed z-[100] bg-gray-900 text-white text-sm px-4 py-3 rounded-lg shadow-2xl pointer-events-none"
          style={{ 
            left: tooltipData.x + 15, 
            top: tooltipData.y - 80,
            transform: 'translateX(-50%)'
          }}
        >
          <div className="flex flex-col gap-1.5">
            <div className="text-xs text-gray-400 font-medium mb-1">{props.name}</div>
            {tooltipData.edge === 'start' && (
              <div className="flex items-center gap-3">
                <span className="text-gray-400 text-xs uppercase tracking-wide">Start:</span>
                <span className="font-bold text-base">{calculateDate(tooltipData.startDay)}</span>
              </div>
            )}
            {tooltipData.edge === 'end' && (
              <div className="flex items-center gap-3">
                <span className="text-gray-400 text-xs uppercase tracking-wide">End:</span>
                <span className="font-bold text-base">{calculateDate(tooltipData.endDay)}</span>
              </div>
            )}
            <div className="flex items-center justify-between text-[11px] text-gray-400 mt-2 pt-2 border-t border-gray-700">
              <span>Duration:</span>
              <span className="font-semibold text-white">{tooltipData.endDay - tooltipData.startDay} days</span>
            </div>
          </div>
          {/* Arrow */}
          <div className="absolute left-1/2 -bottom-2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-gray-900" />
        </div>
      )}
      
      <div className="group relative h-10 md:h-12 flex items-center">
        <div className="absolute left-0 w-36 md:w-52 pr-3 md:pr-4 text-right">
          <p className="text-xs md:text-sm font-medium truncate">{props.name}</p>
          <p className="text-[10px] md:text-xs text-muted-foreground">
            {props.qty > 1 ? `${props.qty}× ` : ""}{durationDays} days
          </p>
        </div>
        <div className="ml-36 md:ml-52 flex-1 h-full relative">
          {/* Background track with day markers */}
          <div className="absolute inset-0 bg-secondary/20 rounded-md border border-border/30" />
          
          {/* The draggable bar */}
          <div 
            ref={barRef}
            className={`absolute h-8 md:h-9 top-1 md:top-1.5 rounded-md ${colors.bar} shadow-md transition-shadow group-hover:shadow-lg cursor-pointer touch-manipulation select-none ${isDragging ? 'opacity-50' : ''} ${isResizing ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
            style={{ left: `${leftPercent}%`, width: `${Math.max(widthPercent, 3)}%` }}
            title={`${props.name}: ${calculateDate(props.startDay)} - ${calculateDate(props.endDay)}`}
            draggable={!!props.onDrag}
            onDragStart={handleDragStart}
            onDragEnd={() => setIsDragging(false)}
            onClick={handleClick}
          >
            {/* Left resize handle */}
            {props.onResize && (
              <div 
                className="absolute left-0 top-0 bottom-0 w-3 cursor-ew-resize hover:bg-white/40 rounded-l-md flex items-center justify-center transition-colors"
                onMouseDown={(e) => handleResizeStart(e, 'start')}
              >
                <div className="w-0.5 h-4 bg-white/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            )}
            
            {/* Center content */}
            <div className="h-full flex items-center justify-center px-2 overflow-hidden">
              <span className="text-[10px] md:text-xs text-white font-semibold truncate drop-shadow-sm">
                {props.qty > 1 ? `${props.qty}×` : ""} {durationDays}d
              </span>
            </div>
            
            {/* Right resize handle */}
            {props.onResize && (
              <div 
                className="absolute right-0 top-0 bottom-0 w-3 cursor-ew-resize hover:bg-white/40 rounded-r-md flex items-center justify-center transition-colors"
                onMouseDown={(e) => handleResizeStart(e, 'end')}
              >
                <div className="w-0.5 h-4 bg-white/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// Phase Card Component for Board View
function PhaseCard(props) {
  const colors = CATEGORY_COLORS[props.category] || CATEGORY_COLORS["Interior Renders"];
  
  return (
    <div className={`p-3 md:p-4 rounded-xl border-2 ${colors.border} ${colors.bg} transition-all hover:shadow-lg`}>
      <div className="flex items-center justify-between mb-2 md:mb-3">
        <Badge className={`${colors.bg} ${colors.text} rounded-full font-medium text-xs`}>{props.category}</Badge>
        <span className="text-[10px] md:text-xs text-muted-foreground">{props.deliverables.length} items</span>
      </div>
      
      <div className="space-y-1.5 md:space-y-2 mb-3 md:mb-4">
        {props.deliverables.slice(0, 4).map(function(d, i) {
          return (
            <div key={i} className="flex items-center justify-between text-xs md:text-sm bg-white/60 rounded-lg px-2 md:px-3 py-1.5 md:py-2">
              <span className="truncate flex-1 mr-2">{d.deliverable_type}</span>
              <span className="text-[10px] md:text-xs text-muted-foreground shrink-0">{d.quantity}x</span>
            </div>
          );
        })}
        {props.deliverables.length > 4 && (
          <p className="text-[10px] md:text-xs text-muted-foreground text-center">+{props.deliverables.length - 4} more</p>
        )}
      </div>
      
      <div className="pt-2 md:pt-3 border-t border-white/50">
        <div className="flex items-center justify-between text-xs md:text-sm">
          <div className="flex items-center gap-1 text-muted-foreground">
            <Clock className="w-3 md:w-3.5 h-3 md:h-3.5" />
            <span>{props.totalDays.toFixed(1)} days</span>
          </div>
          <div className="flex items-center gap-1 text-muted-foreground">
            <Calendar className="w-3 md:w-3.5 h-3 md:h-3.5" />
            <span>{props.startDate || `Day ${props.startDay}`}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function TimelineView() {
  const params = useParams();
  const programId = params.id;
  
  const [timeline, setTimeline] = useState(null);
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("table");
  
  // Track temporary resize state
  const [resizeState, setResizeState] = useState({});

  const fetchData = useCallback(function() {
    setLoading(true);
    setError(null);
    
    Promise.all([
      apiClient.get("/programs/" + programId),
      apiClient.get("/programs/" + programId + "/timeline")
    ]).then(function(results) {
      setProgram(results[0].data);
      setTimeline(results[1].data);
      setLoading(false);
    }).catch(function(err) {
      setError("Failed to load timeline data");
      toast.error("Failed to load timeline");
      setLoading(false);
    });
  }, [programId]);

  useEffect(function() {
    fetchData();
  }, [fetchData]);

  function handleSaveOverride(deliverableId, data) {
    return apiClient.post("/programs/" + programId + "/timeline/override", {
      deliverable_id: deliverableId,
      quantity: data.quantity,
      production_days_per_unit: data.production_days_per_unit,
      category: data.category
    }).then(function() {
      toast.success("Timeline updated");
      fetchData();
    }).catch(function(err) {
      toast.error("Failed to save");
      throw err;
    });
  }

  function handleResetOverride(deliverableId) {
    return apiClient.delete("/programs/" + programId + "/timeline/override/" + deliverableId)
      .then(function() {
        toast.success("Reset to auto-calculated");
        fetchData();
      }).catch(function(err) {
        toast.error("Failed to reset");
        throw err;
      });
  }
  
  // Handle Gantt bar resize (temporary state during resize)
  function handleGanttResize(deliverableId, newStartDay, newEndDay) {
    setResizeState(prev => ({
      ...prev,
      [deliverableId]: { startDay: newStartDay, endDay: newEndDay }
    }));
  }
  
  // Save resize to backend - now receives final days directly from callback
  function handleGanttResizeEnd(deliverableId, finalStartDay, finalEndDay, deliverable) {
    // Use passed parameters directly instead of relying on React state
    const newTotalDays = finalEndDay - finalStartDay;
    const newDaysPerUnit = newTotalDays / (deliverable.quantity || 1);
    
    // Save the override
    handleSaveOverride(deliverableId, {
      quantity: deliverable.quantity,
      production_days_per_unit: newDaysPerUnit,
      category: deliverable.category
    }).then(() => {
      // Clean up resize state
      setResizeState(prev => {
        const newState = { ...prev };
        delete newState[deliverableId];
        return newState;
      });
    });
  }
  
  // Handle click on Gantt bar to open edit dialog
  function handleGanttBarClick(deliverableId) {
    // Find the deliverable and trigger edit mode
    // For now, just show a toast - can expand to open edit dialog
    toast.info("Click on Table view rows to edit, or drag bar edges to resize");
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="timeline-loading">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !timeline || !program) {
    return (
      <div className="max-w-6xl mx-auto" data-testid="timeline-error">
        <Link to="/programs" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Programs
        </Link>
        <p className="text-muted-foreground">{error || "Timeline not available"}</p>
      </div>
    );
  }

  // Combine all deliverables for views
  const allDeliverables = [...(timeline.timeline || []), ...(timeline.marketing_timeline || [])];
  const allPhases = [...(timeline.phases || []), ...(timeline.marketing_phases || [])];
  const maxDay = Math.max(...allDeliverables.map(d => d.end_day), 1);

  // Group deliverables by category for board view
  const deliverablesByCategory = {};
  allDeliverables.forEach(function(d) {
    if (!deliverablesByCategory[d.category]) {
      deliverablesByCategory[d.category] = [];
    }
    deliverablesByCategory[d.category].push(d);
  });

  return (
    <div className="max-w-6xl mx-auto" data-testid="timeline-view">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <Link 
          to={"/programs/" + programId} 
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back to {program.program_name}
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-4xl font-light tracking-tight text-foreground" style={{ fontFamily: 'var(--font-sans)' }}>Production Timeline</h1>
            <p className="body-text mt-1">{program.program_name}</p>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            {/* View Toggle */}
            <div className="flex items-center p-1 bg-secondary/80 rounded-lg" data-testid="view-toggle">
              <button
                onClick={() => setViewMode("table")}
                className={`flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === "table" 
                    ? "bg-white text-foreground shadow-sm" 
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <List className="w-4 h-4" />
                <span className="hidden sm:inline">Table</span>
              </button>
              <button
                onClick={() => setViewMode("gantt")}
                className={`flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === "gantt" 
                    ? "bg-white text-foreground shadow-sm" 
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                <span className="hidden sm:inline">Gantt</span>
              </button>
              <button
                onClick={() => setViewMode("board")}
                className={`flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === "board" 
                    ? "bg-white text-foreground shadow-sm" 
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Columns className="w-4 h-4" />
                <span className="hidden sm:inline">Board</span>
              </button>
            </div>
            <Button variant="outline" onClick={fetchData} className="rounded-full px-3 md:px-4" data-testid="recalculate-btn">
              <RefreshCw className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Refresh</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards - Scrollable on mobile */}
      <div className="flex gap-3 md:grid md:grid-cols-5 md:gap-4 mb-6 md:mb-8 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
        <Card className="flex-shrink-0 w-32 md:w-auto border border-blue-200 bg-blue-50">
          <CardContent className="p-3 md:p-4">
            <p className="text-[10px] md:text-xs text-blue-600 font-medium mb-1">Production</p>
            <p className="text-2xl font-light text-blue-700" style={{ fontFamily: 'var(--font-sans)' }}>
              {(timeline.production_days || 0).toFixed(1)}
              <span className="text-xs md:text-sm font-normal ml-1">days</span>
            </p>
          </CardContent>
        </Card>
        <Card className="flex-shrink-0 w-32 md:w-auto border border-teal-200 bg-teal-50">
          <CardContent className="p-3 md:p-4">
            <p className="text-[10px] md:text-xs text-teal-600 font-medium mb-1">VEXOS</p>
            <p className="text-xl md:text-2xl font-light text-teal-700" style={{ fontFamily: 'var(--font-sans)' }}>
              {allPhases.filter(p => p.category === "VEXOS").reduce((sum, p) => sum + p.total_days, 0).toFixed(1)}
              <span className="text-xs md:text-sm font-normal ml-1">days</span>
            </p>
          </CardContent>
        </Card>
        <Card className="flex-shrink-0 w-32 md:w-auto border border-pink-200 bg-pink-50">
          <CardContent className="p-3 md:p-4">
            <p className="text-[10px] md:text-xs text-pink-600 font-medium mb-1">Marketing</p>
            <p className="text-xl md:text-2xl font-light text-pink-700" style={{ fontFamily: 'var(--font-sans)' }}>
              {(timeline.marketing_days || 0).toFixed(1)}
              <span className="text-xs md:text-sm font-normal ml-1">days</span>
            </p>
          </CardContent>
        </Card>
        <Card className="flex-shrink-0 w-32 md:w-auto border border-orange-200 bg-orange-50">
          <CardContent className="p-3 md:p-4">
            <p className="text-[10px] md:text-xs text-orange-600 font-medium mb-1">Buffer (25%)</p>
            <p className="text-xl md:text-2xl font-light text-orange-700" style={{ fontFamily: 'var(--font-sans)' }}>
              +{timeline.revision_buffer_days.toFixed(1)}
              <span className="text-xs md:text-sm font-normal ml-1">days</span>
            </p>
          </CardContent>
        </Card>
        <Card className="flex-shrink-0 w-32 md:w-auto border border-primary/30 bg-primary/10">
          <CardContent className="p-3 md:p-4">
            <p className="text-[10px] md:text-xs text-primary font-medium mb-1">Grand Total</p>
            <p className="text-xl md:text-2xl font-light text-primary" style={{ fontFamily: 'var(--font-sans)' }}>
              {timeline.grand_total_days.toFixed(1)}
              <span className="text-xs md:text-sm font-normal ml-1">days</span>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Table View */}
      {viewMode === "table" && (
        <div className="space-y-4 md:space-y-6">
          {allPhases.map(function(phase, phaseIdx) {
            const phaseDeliverables = allDeliverables.filter(d => d.category === phase.category);
            const colors = CATEGORY_COLORS[phase.category] || CATEGORY_COLORS["Interior Renders"];
            
            return (
              <Card key={phaseIdx} className={`border-2 ${colors.border} overflow-hidden`}>
                <CardHeader className={`${colors.bg} py-3 md:py-4 px-3 md:px-6`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className={`w-2.5 md:w-3 h-2.5 md:h-3 rounded-full ${colors.bar}`} />
                      <CardTitle className={`text-sm md:text-base ${colors.text}`} style={{ fontFamily: 'var(--font-sans)' }}>
                        {phase.category}
                      </CardTitle>
                      <Badge className="bg-white/60 text-foreground rounded-full text-[10px] md:text-xs">
                        {phase.deliverable_count} items
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 md:gap-4 text-xs md:text-sm">
                      <span className={colors.text}><strong>{phase.total_days.toFixed(1)}</strong> days</span>
                      {phase.start_date && (
                        <span className="text-muted-foreground hidden sm:inline">{phase.start_date} → {phase.end_date}</span>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full text-xs md:text-sm min-w-[600px]">
                    <thead className="bg-secondary/30">
                      <tr>
                        <th className="text-left px-3 md:px-4 py-2 font-medium text-[10px] md:text-xs text-muted-foreground">Deliverable</th>
                        <th className="text-center px-3 md:px-4 py-2 font-medium text-[10px] md:text-xs text-muted-foreground">Category</th>
                        <th className="text-center px-3 md:px-4 py-2 font-medium text-[10px] md:text-xs text-muted-foreground">Qty</th>
                        <th className="text-center px-3 md:px-4 py-2 font-medium text-[10px] md:text-xs text-muted-foreground">Days/Unit</th>
                        <th className="text-center px-3 md:px-4 py-2 font-medium text-[10px] md:text-xs text-muted-foreground">Total</th>
                        <th className="text-left px-3 md:px-4 py-2 font-medium text-[10px] md:text-xs text-muted-foreground">Schedule</th>
                      </tr>
                    </thead>
                    <tbody>
                      {phaseDeliverables.map(function(item) {
                        const schedule = item.start_date 
                          ? (item.start_date + " → " + item.end_date)
                          : ("Day " + item.start_day + " - " + item.end_day);
                        
                        return (
                          <EditableRow
                            key={item.deliverable_id}
                            deliverableId={item.deliverable_id}
                            type={item.deliverable_type}
                            category={item.category}
                            qty={item.quantity}
                            perUnit={item.production_days_per_unit}
                            totalDays={item.total_production_days.toFixed(1)}
                            schedule={schedule}
                            isCustom={item.is_custom}
                            onSave={handleSaveOverride}
                            onReset={handleResetOverride}
                          />
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Gantt Chart View */}
      {viewMode === "gantt" && (
        <Card className="border border-border overflow-hidden">
          <CardHeader className="bg-secondary/30 py-3 md:py-4 px-3 md:px-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <CardTitle className="text-sm md:text-base" style={{ fontFamily: 'var(--font-sans)' }}>Production Schedule</CardTitle>
                <p className="text-[10px] md:text-xs text-muted-foreground mt-1">Drag bar edges to resize • Click bar for details</p>
              </div>
              <div className="flex items-center gap-2 text-[10px] md:text-xs text-muted-foreground">
                <Calendar className="w-3.5 h-3.5 md:w-4 md:h-4" />
                <span className="hidden sm:inline">{timeline.start_date || "Start"} —</span> Day {maxDay} ({timeline.total_calendar_days} days)
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-3 md:p-6 overflow-x-auto">
            {/* Timeline Header with dates */}
            <div className="min-w-[500px]">
              <div className="flex items-center mb-4 ml-36 md:ml-52">
                <div className="flex-1 flex items-center justify-between text-[10px] md:text-xs text-muted-foreground">
                  {[0, 0.25, 0.5, 0.75, 1].map((fraction, idx) => {
                    const day = Math.floor(maxDay * fraction);
                    let dateStr = `Day ${day}`;
                    if (timeline.start_date) {
                      const d = new Date(timeline.start_date);
                      d.setDate(d.getDate() + day);
                      dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    }
                    return <span key={idx}>{dateStr}</span>;
                  })}
                </div>
              </div>
              
              {/* Phase Groups */}
              <div className="space-y-4 md:space-y-6">
                {allPhases.map(function(phase, idx) {
                  const phaseDeliverables = allDeliverables.filter(d => d.category === phase.category);
                  const colors = CATEGORY_COLORS[phase.category] || CATEGORY_COLORS["Interior Renders"];
                  
                  return (
                    <div key={idx}>
                      <div className="flex items-center gap-2 mb-2 md:mb-3">
                        <div className={`w-2.5 md:w-3 h-2.5 md:h-3 rounded-full ${colors.bar}`} />
                        <span className={`text-xs md:text-sm font-medium ${colors.text}`}>{phase.category}</span>
                        <span className="text-[10px] md:text-xs text-muted-foreground">({phase.total_days.toFixed(1)} days)</span>
                      </div>
                      <div className="space-y-1.5 md:space-y-2 ml-3 md:ml-5">
                        {phaseDeliverables.map(function(d, dIdx) {
                          const tempState = resizeState[d.deliverable_id];
                          const startDay = tempState ? tempState.startDay : d.start_day;
                          const endDay = tempState ? tempState.endDay : d.end_day;
                          
                          return (
                            <GanttBar
                              key={dIdx}
                              deliverableId={d.deliverable_id}
                              name={d.deliverable_type}
                              category={d.category}
                              qty={d.quantity}
                              days={d.total_production_days.toFixed(1)}
                              startDay={startDay}
                              endDay={endDay}
                              totalDays={maxDay}
                              programStartDate={timeline.start_date}
                              onResize={handleGanttResize}
                              onResizeEnd={(deliverableId, finalStart, finalEnd) => handleGanttResizeEnd(deliverableId, finalStart, finalEnd, d)}
                              onClick={handleGanttBarClick}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="mt-8 pt-6 border-t border-border">
              <p className="text-xs text-muted-foreground mb-3">Legend</p>
              <div className="flex flex-wrap gap-4">
                {Object.entries(CATEGORY_COLORS).map(function([cat, colors]) {
                  const hasItems = allPhases.some(p => p.category === cat);
                  if (!hasItems) return null;
                  return (
                    <div key={cat} className="flex items-center gap-2">
                      <div className={`w-4 h-3 rounded ${colors.bar}`} />
                      <span className="text-xs text-muted-foreground">{cat}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Board View */}
      {viewMode === "board" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
          {allPhases.map(function(phase, idx) {
            const phaseDeliverables = allDeliverables.filter(d => d.category === phase.category);
            
            return (
              <PhaseCard
                key={idx}
                category={phase.category}
                deliverables={phaseDeliverables}
                totalDays={phase.total_days}
                startDay={phase.start_day}
                endDay={phase.end_day}
                startDate={phase.start_date}
                endDate={phase.end_date}
              />
            );
          })}
        </div>
      )}

      {/* Footer Note */}
      <p className="text-[10px] md:text-xs text-muted-foreground italic text-center mt-6 md:mt-8 px-4">
        * Timeline auto-updates when deliverables are modified. In Gantt view, drag bar edges to resize phases. In Table view, click any row to customize.
      </p>
    </div>
  );
}

export default TimelineView;
